import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

const PropertyModal = ({ property, onClose }) => {
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    // Load all images for the property
    const loadImages = async () => {
      let imageFiles = [];
      
      if (property.id === 'cabana-concept') {
        imageFiles = [
          'Capa.jpg', 'IMG_0859.jpg', 'IMG_0909.jpg', 'IMG_0931.jpg',
          'IMG_0942.jpg', 'IMG_0945.jpg', 'IMG_0970.jpg', 'IMG_1209_1.jpg',
          'IMG_1217.jpg', 'IMG_1219.jpg'
        ];
      } else if (property.id === 'cabana-aurora') {
        imageFiles = [
          'IMG_0065.jpg', 'IMG_0094.jpg', 'IMG_0109.jpg', 'IMG_0147.jpg',
          'IMG_0641.jpg', 'IMG_0815.jpg', 'IMG_1078.jpg', 'IMG_1115.jpg',
          'IMG_1127.jpg', 'IMG_1138.jpg'
        ];
      } else if (property.id === 'casa-m5') {
        imageFiles = [
          '0905f41d-f5e8-483d-ac6f-cb1dfc2957bc.jpg',
          '45e72ac0-1b4e-4e58-9e37-7cdc8441fb64.jpg'
        ];
      }
      
      const folderName = property.id === 'cabana-concept' ? 'Cabana-Concept' : 
                        property.id === 'cabana-aurora' ? 'Cabana-Aurora' : 'Casa-M5';
      
      setImages(imageFiles.map(file => `/images/${folderName}/${file}`));
    };
    
    loadImages();
  }, [property]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-3xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition-colors"
          >
            <X size={24} className="text-primary" />
          </button>

          <div className="overflow-y-auto max-h-[90vh]">
            {/* Image Gallery */}
            <div className="relative h-[400px] md:h-[500px] group">
              <img
                src={images[currentImageIndex] || property.mainImage}
                alt={property.name}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    data-testid="prev-image-btn"
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                  >
                    <ChevronLeft size={24} className="text-primary" />
                  </button>
                  <button
                    data-testid="next-image-btn"
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                  >
                    <ChevronRight size={24} className="text-primary" />
                  </button>
                </>
              )}
              
              {/* Image Counter */}
              <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
              
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    data-testid={`image-indicator-${index}`}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex
                        ? 'bg-white w-8'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent mb-2">
                    {property.type}
                  </p>
                  <h2 className="text-4xl md:text-5xl font-light tracking-tight text-primary mb-4">
                    {property.name}
                  </h2>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Users size={20} />
                      <span>Até {property.capacity} pessoas</span>
                    </div>
                  </div>
                </div>
                <div className="bg-accent text-white px-6 py-4 rounded-2xl text-center">
                  <p className="text-2xl font-semibold">
                    R$ {property.price.toLocaleString('pt-BR')}
                  </p>
                  <p className="text-sm opacity-90">por diária</p>
                </div>
              </div>

              <p className="text-lg leading-relaxed text-muted-foreground mb-8">
                {property.description}
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-2xl font-normal text-primary mb-4">Destaques</h3>
                  <ul className="space-y-3">
                    {property.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle size={20} className="text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-base text-foreground/80">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-normal text-primary mb-4">Comodidades</h3>
                  <ul className="space-y-3">
                    {property.amenities.map((amenity, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle size={20} className="text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-base text-foreground/80">{amenity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  data-testid="modal-booking-btn"
                  onClick={() => {
                    onClose();
                    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-accent text-white hover:bg-accent/90 rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all hover:scale-105"
                >
                  Solicitar Reserva
                </button>
                <a
                  data-testid="modal-whatsapp-btn"
                  href="https://wa.me/5521996273547"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-white rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PropertyModal;